"use client";

import { useState, useMemo, useEffect } from "react";
import { equipmentCategoriesData } from "@/data/pages/equipment";
import EquipmentCard from "./EquipmentCard";
import ScrollReveal from "@/components/ui/ScrollReveal";
import CategorySearchFilter from "@/components/ui/CategorySearchFilter";
import { capabilitiesPageData } from "@/data";

export default function EquipmentGridSection({ id = "equipment" }: { id?: string }) {

  // Smooth scroll to equipment section when URL hash is #equipment
  useEffect(() => {
    const isEquipmentHash = () => {
      if (typeof window === "undefined") return false;
      return window.location.hash.toLowerCase() === "#equipment";
    };

    const scrollToSection = () => {
      if (!isEquipmentHash()) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    if (isEquipmentHash()) {
      scrollToSection();
      const t1 = setTimeout(scrollToSection, 100);
      const t2 = setTimeout(scrollToSection, 300);
      const t3 = setTimeout(scrollToSection, 600);

      window.addEventListener("hashchange", scrollToSection);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        window.removeEventListener("hashchange", scrollToSection);
      };
    }

    window.addEventListener("hashchange", scrollToSection);
    return () => {
      window.removeEventListener("hashchange", scrollToSection);
    };
  }, [id]);

  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const categoryOptions = useMemo(() => {
    return [
      { value: "all", label: "Categories" },
      ...equipmentCategoriesData.map((cat) => ({
        value: cat.slug,
        label: cat.shortTitle || cat.title,
      })),
    ];
  }, []);

  const filteredCategories = useMemo(() => {
    return equipmentCategoriesData.filter((cat) => {
      const matchesCategory =
        selectedCategorySlug === "all" || cat.slug === selectedCategorySlug;
      const matchesSearch =
        searchQuery.trim() === "" ||
        cat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (cat.shortTitle &&
          cat.shortTitle.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (cat.description &&
          cat.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (cat.items &&
          cat.items.some(
            (item) =>
              item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (item.items &&
                item.items.some((subItem) =>
                  subItem.title.toLowerCase().includes(searchQuery.toLowerCase())
                ))
          ));

      return matchesCategory && matchesSearch;
    });
  }, [selectedCategorySlug, searchQuery]);

  return (
    <div id={id} className="w-full scroll-mt-28 md:scroll-mt-36">
      {/* Header Bar with Category Filter & Search */}
      <ScrollReveal variant="fade-up" duration={700} className="relative z-30">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 sm:mb-10">
          {/* Left: Indicator + Title */}
          <div className="flex items-center gap-2.5 shrink-0">
            <h1 className="font-poppins text-2xl sm:text-[26px] lg:text-[32px] font-regular text-white tracking-tight leading-[1.2]">
              {capabilitiesPageData.equipmentTitle}
            </h1>
          </div>

          {/* Right: Unified Filter & Search Bar */}
          <CategorySearchFilter
            options={categoryOptions}
            selectedValue={selectedCategorySlug}
            onCategoryChange={setSelectedCategorySlug}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            placeholder="Categories"
            searchPlaceholder="Search"
            dropdownAriaLabel="Filter by category"
          />
        </div>
      </ScrollReveal>

      {/* Grid of Equipment Cards */}
      {filteredCategories.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {filteredCategories.map((category, idx) => (
            <ScrollReveal
              key={category.id}
              variant="fade-up"
              delay={(idx % 6) * 75}
              duration={600}
            >
              <EquipmentCard category={category} />
            </ScrollReveal>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 px-4 bg-[#1c1e21] border border-white/10 rounded-[10px]">
          <p className="font-poppins text-white/60 text-base mb-4">
            No equipment categories found matching &quot;{searchQuery}&quot;.
          </p>
          <button
            type="button"
            onClick={() => {
              setSearchQuery("");
              setSelectedCategorySlug("all");
            }}
            className="font-poppins text-xs sm:text-sm text-brand-green hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
